import Phaser from 'phaser';
import './style.css';
import { BootScene } from './game/scenes/BootScene';
import { MenuScene } from './game/scenes/MenuScene';
import { GameScene } from './game/scenes/GameScene';
import { PostcardScene } from './game/scenes/PostcardScene';
import { ResultScene } from './game/scenes/ResultScene';
import { GameOverScene } from './game/scenes/GameOverScene';
import { introMusic } from './game/systems/IntroMusic';
import { audio } from './game/systems/AudioManager';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH, RENDER_SCALE } from './game/systems/DisplayManager';
import { ProgressStore } from './game/systems/ProgressStore';
import { LEVELS } from './content/levels';

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game',
  width: LOGICAL_WIDTH * RENDER_SCALE,
  height: LOGICAL_HEIGHT * RENDER_SCALE,
  backgroundColor: '#071727',
  pixelArt: false,
  roundPixels: false,
  render: {
    antialias: true,
    pixelArt: false,
    roundPixels: false,
  },
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    fullscreenTarget: 'game',
  },
  physics: {
    default: 'arcade',
    arcade: {
      gravity: { x: 0, y: 760 },
      debug: false,
    },
  },
  scene: [BootScene, MenuScene, GameScene, PostcardScene, ResultScene, GameOverScene],
};

const game = new Phaser.Game(config);

const initialSave = new ProgressStore().load();
audio.setMuted(initialSave.muted);
introMusic.setMuted(initialSave.muted);
introMusic.prime();

const unlockAudio = (): void => {
  audio.resume();
  introMusic.unlock();
};

window.addEventListener('keydown', unlockAudio, { capture: true, once: true });
window.addEventListener('pointerup', unlockAudio, { capture: true, once: true });

const fullscreenButton = document.getElementById('fullscreen-toggle') as HTMLButtonElement | null;
const toggleFullscreen = (): void => game.scale.toggleFullscreen();
fullscreenButton?.addEventListener('pointerup', toggleFullscreen);
window.addEventListener('keydown', (event) => {
  if (event.code !== 'KeyF' || event.repeat) return;
  event.preventDefault();
  toggleFullscreen();
});
document.addEventListener('fullscreenchange', () => {
  if (!fullscreenButton) return;
  fullscreenButton.textContent = document.fullscreenElement ? '⛶ WYJDŹ Z PEŁNEGO EKRANU [F]' : '⛶ PEŁNY EKRAN [F]';
});

export interface GameDebugApi {
  game: Phaser.Game;
  startLevel(index: number, mode?: 'solo' | 'coop'): void;
  freeze(): void;
  hitBoss(count?: number): void;
  popBoss(): void;
  protectPlayers(): void;
  menuSnapshot(): ReturnType<MenuScene['getDebugSnapshot']> | null;
  snapshot(): ReturnType<GameScene['getDebugSnapshot']> | null;
}

declare global {
  interface Window {
    __BABELKI__?: GameDebugApi;
  }
}

if (import.meta.env.DEV) {
  window.__BABELKI__ = {
    game,
    startLevel(index, mode = 'solo') {
      introMusic.stop();
      game.scene.start('Game', { mode, levelIndex: Phaser.Math.Clamp(index, 0, LEVELS.length - 1), score: 0, multiplier: 1 });
    },
    freeze() {
      const scene = game.scene.getScene('Game');
      if (scene instanceof GameScene && scene.sys.isActive()) scene.debugFreeze();
    },
    hitBoss(count = 1) {
      const scene = game.scene.getScene('Game');
      if (scene instanceof GameScene && scene.sys.isActive()) scene.debugHitBoss(count);
    },
    popBoss() {
      const scene = game.scene.getScene('Game');
      if (scene instanceof GameScene && scene.sys.isActive()) scene.debugPopBoss();
    },
    protectPlayers() {
      const scene = game.scene.getScene('Game');
      if (scene instanceof GameScene && scene.sys.isActive()) scene.debugProtectPlayers();
    },
    menuSnapshot() {
      const scene = game.scene.getScene('Menu');
      return scene instanceof MenuScene && scene.sys.isActive() ? scene.getDebugSnapshot() : null;
    },
    snapshot() {
      const scene = game.scene.getScene('Game');
      return scene instanceof GameScene && scene.sys.isActive() ? scene.getDebugSnapshot() : null;
    },
  };
}
